import { UserManager } from 'oidc-client-ts';

import { currentAppEnvironment } from '@savgroup-front-common/constants/shared';

import { delay } from './delay';

function clearOidcStaleState(userManager: UserManager) {
  return (
    userManager
      .clearStaleState()
      // eslint-disable-next-line no-console
      .catch((err: unknown) => console.log('clearOidcStaleState', err))
  );
}

function css(color: string) {
  return `background: ${color}; border-radius: 2px; padding: 3px 8px; color: white;`;
}

function setupRetryStrategyForSilentRenewal(userManager: UserManager) {
  const RETRY_DELAY = 60 * 1000;

  let retryTask: number | undefined;

  userManager.events.addSilentRenewError((e: Error) => {
    // eslint-disable-next-line no-console
    console.warn('silent renew error', e.message);
    if (retryTask !== undefined) {
      window.clearTimeout(retryTask);
    }
    userManager.stopSilentRenew();
    retryTask = window.setTimeout(
      () => userManager.startSilentRenew(),
      RETRY_DELAY,
    );
  });
}

interface AppShellArgs {
  userManager: UserManager;
  render: () => void;
}

export const appShell = async ({
  userManager,
  render,
}: AppShellArgs): Promise<void> => {
  if (window.location.pathname === '/silent-renew') {
    await userManager.getUser();
    userManager.signinSilentCallback();

    return;
  }

  setupRetryStrategyForSilentRenewal(userManager);
  await clearOidcStaleState(userManager);
  await Promise.all([
    (async function frequentlyClearStaleState() {
      while (true) {
        await delay(30 * 1000);
        await clearOidcStaleState(userManager);
      }
    })(),
    (async function app() {
      // eslint-disable-next-line no-console
      if (typeof console.groupCollapsed === 'function') {
        // eslint-disable-next-line no-console
        console.groupCollapsed('App info');
      }
      // eslint-disable-next-line no-console
      console.info('%cBuild mode', css('green'), currentAppEnvironment);
      // eslint-disable-next-line no-console
      console.info(
        '%cBuild Id',
        css('blue'),
        `${import.meta.env.VITE_BUILD_ID}`,
      );

      if (import.meta.env.VITE_BUILD_NUMBER) {
        // eslint-disable-next-line no-console
        console.info(
          '%cBuild Number',
          css('red'),
          `${import.meta.env.VITE_BUILD_NUMBER}`,
        );
      }

      if (import.meta.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          "Messages that start with '[React Intl] Missing message:' have been disabled. See index.js to enable them.",
        );
        // eslint-disable-next-line no-console
        const originalConsoleError = console.error;

        // eslint-disable-next-line no-console
        if (console.error === originalConsoleError) {
          // eslint-disable-next-line no-console
          console.error = (...args: unknown[]) => {
            const [firstArg] = args;

            if (
              !args.length ||
              typeof (firstArg as { indexOf?: unknown } | undefined)?.indexOf !==
                'function'
            ) {
              originalConsoleError.call(console, ...args);

              return;
            }

            const message = firstArg as string;

            if (message.indexOf('[React Intl] Missing message:') === 0) {
              return;
            }
            if (
              message.indexOf('[@formatjs/intl Error MISSING_TRANSLATION]') === 0
            ) {
              return;
            }
            if (
              message.indexOf(
                'Warning: Unknown event handler property `onHide`. It will be ignored.',
              ) === 0
            ) {
              return;
            }
            originalConsoleError.call(console, ...args);
          };
        }
      }

      // eslint-disable-next-line no-console
      if (typeof console.group === 'function') {
        // eslint-disable-next-line no-console
        console.groupEnd();
      }

      await render();
    })(),
  ]);
};
